import { Col, Form, Row } from 'react-bootstrap'
import { Controller, useFormContext } from 'react-hook-form'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import React, { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSelectedZocksContact, selectShowCreateContact, selectShowLinkContact } from '../../../store/meetingSummary/contactUpdate'
import { User } from '../../../models/User'
import { CRMAccountIntegrationConfig, DescriptorType } from '../../../models/Account'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../common/integrations'

type FormFields = {
  firstName: boolean
  lastName: boolean
  name: boolean
  email: boolean
}

type FormFieldsMap = {
  [serviceId: string]: {
    [contactObject: string]: FormFields
  }
}

const DEFAULT_FORM_FIELDS = {
  firstName: true,
  lastName: true,
  name: false,
  email: true,
}

const FORM_FIELDS_EXCEPTIONS: FormFieldsMap = {
  salesforce: {
    account: { firstName: false, lastName: false, name: true, email: false },
  },
  practifi: {
    account: { firstName: false, lastName: false, name: true, email: false },
  },
  xlr8: {
    account: { firstName: false, lastName: false, name: true, email: false },
  },
  zoho: {
    account: { firstName: false, lastName: false, name: true, email: false },
  },
}

const getFormFields = (serviceId: string, contactObject: string): FormFields => {
  const exception = FORM_FIELDS_EXCEPTIONS[serviceId]?.[contactObject]

  return exception || DEFAULT_FORM_FIELDS
}

export type UpdateExternalContactLinkSectionProps = {
  serviceId: IntegrationServiceId
}

export const UpdateExternalContactCreateSection: React.FC<UpdateExternalContactLinkSectionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const showCreateZocksContact = useAppSelector(selectShowCreateContact)
  const showLinkToExistingZocksContact = useAppSelector(selectShowLinkContact)
  const selectedZocksContact = useAppSelector(selectSelectedZocksContact)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const form = useFormContext()
  const integration = account?.data?.config.integrations.find((i) => i.serviceId === serviceId && i.type === 'crm') as CRMAccountIntegrationConfig | undefined
  const contactMapping = integration?.mappings?.contact ?? null
  const contactObject = contactMapping && typeof contactMapping === 'object' && 'objectName' in contactMapping ? contactMapping.objectName : 'contact'
  const emailFields = typeof contactMapping !== 'string' ? (contactMapping?.fields?.email ?? null) : null
  const customEmailField = emailFields?.find((field) => field.type === DescriptorType.FIELD)?.value ?? null
  const { firstName: contextFirstName, lastName: contextLastName, name: contextName, email: contextEmail } = getFormFields(serviceId, contactObject)
  const createNewContact = form.watch('createNewContact')
  const initFormWithContact = useCallback(
    (contact: User) => {
      if (!contact) {
        return
      }
      const setEmail = !!(contextEmail || customEmailField)
      if (contextFirstName) form.setValue('firstName', contact.firstName || '')
      if (contextLastName) form.setValue('lastName', contact.lastName || '')
      if (contextName) form.setValue('name', contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : '')
      if (setEmail) form.setValue('email', contact.email || '')
    },
    [contextEmail, contextFirstName, contextLastName, contextName, customEmailField, form],
  )

  useEffect(() => {
    if (selectedZocksContact) {
      initFormWithContact(selectedZocksContact)
    }
  }, [form, initFormWithContact, createNewContact, dispatch, selectedZocksContact])

  return (
    <>
      {contextFirstName ? (
        <Row>
          <Col>
            <Controller
              name='firstName'
              control={form.control}
              rules={{ required: 'First Name is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control id={field.name} type='text' placeholder='First Name…' {...field} isInvalid={!!fieldState.error} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      ) : null}
      {contextLastName ? (
        <Row>
          <Col>
            <Controller
              name='lastName'
              control={form.control}
              rules={{ required: 'Last Name is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control id={field.name} type='text' placeholder='Last Name…' {...field} isInvalid={!!fieldState.error} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      ) : null}
      {contextName ? (
        <Row>
          <Col>
            <Controller
              name='name'
              control={form.control}
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Label>Name</Form.Label>
                  <Form.Control id={field.name} type='text' placeholder='Name…' {...field} isInvalid={!!fieldState.error} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      ) : null}
      {contextEmail || customEmailField ? (
        <Row>
          <Col>
            <Controller
              name='email'
              control={form.control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Label>Email</Form.Label>
                  <Form.Control id={field.name} type='text' placeholder='Email…' {...field} isInvalid={!!fieldState.error} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      ) : null}
      {showCreateZocksContact ? (
        <Row className='mt-2 mb-4'>
          <Col>
            <Controller
              name='createNewZocksContact'
              control={form.control}
              render={({ field }) => {
                return <Form.Check checked={field.value} onChange={field.onChange} type='checkbox' label='Create the contact in Zocks' />
              }}
            />
          </Col>
        </Row>
      ) : null}
      {showLinkToExistingZocksContact ? (
        <Row className='mt-2 mb-4'>
          <Col>
            <Controller
              name='linkToExistingZocksContact'
              control={form.control}
              render={({ field }) => {
                return <Form.Check checked={field.value} onChange={field.onChange} type='checkbox' label='Link to existing Zocks contact' />
              }}
            />
          </Col>
        </Row>
      ) : null}
      {serviceId === IntegrationServiceId.WEALTH ? (
        <Row className='mt-1 mb-4'>
          <Col>
            <Controller
              name='metadata.sendInvite'
              control={form.control}
              render={({ field }) => (
                <Form.Check
                  id='metadata.sendInvite'
                  type='checkbox'
                  label='Send Wealth.com Client Portal invitation'
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Col>
        </Row>
      ) : null}
    </>
  )
}
